import api from './api';

// Admin payment endpoints
const getPayments = async (params = {}) => {
  try {
    // The merchant is automatically determined from the current logged-in user
    const response = await api.get('/payments/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching payments:', error);
    throw error;
  }
};

const getPayment = async (id) => {
  try {
    const response = await api.get(`/payments/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching payment ${id}:`, error);
    throw error;
  }
};

const createPayment = async (data) => {
  try {
    const response = await api.post('/payments/', data);
    return response.data;
  } catch (error) {
    console.error('Error creating payment:', error);
    throw error;
  }
};

const updatePayment = async (id, data) => {
  try {
    const response = await api.patch(`/payments/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating payment ${id}:`, error);
    throw error;
  }
};

const processPayment = async (id, data) => {
  try {
    const response = await api.post(`/payments/${id}/process/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error processing payment ${id}:`, error);
    throw error;
  }
};

const refundPayment = async (id, data) => {
  try {
    const response = await api.post(`/payments/${id}/refund/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error refunding payment ${id}:`, error);
    throw error;
  }
};

const getPaymentMethods = async (params = {}) => {
  try {
    // The merchant is automatically determined from the current logged-in user
    const response = await api.get('/payments/payment-methods/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching payment methods:', error);
    throw error;
  }
};

const getPaymentMethod = async (id) => {
  try {
    const response = await api.get(`/payments/payment-methods/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching payment method ${id}:`, error);
    throw error;
  }
};

const createPaymentMethod = async (data) => {
  try {
    const response = await api.post('/payments/payment-methods/', data);
    return response.data;
  } catch (error) {
    console.error('Error creating payment method:', error);
    throw error;
  }
};

const updatePaymentMethod = async (id, data) => {
  try {
    const response = await api.patch(`/payments/payment-methods/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating payment method ${id}:`, error);
    throw error;
  }
};

const deletePaymentMethod = async (id) => {
  try {
    const response = await api.delete(`/payments/payment-methods/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting payment method ${id}:`, error);
    throw error;
  }
};

const getPaymentStatusHistory = async (paymentId) => {
  try {
    const response = await api.get('/payments/payment-status-history/', {
      params: { payment: paymentId }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching payment status history for payment ${paymentId}:`, error);
    throw error;
  }
};

const getPaymentStatistics = async (params = {}) => {
  try {
    // The merchant is automatically determined from the current logged-in user
    const response = await api.get('/payments/statistics/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching payment statistics:', error);
    throw error;
  }
};

const getPaymentsByStatus = async (params = {}) => {
  try {
    // The merchant is automatically determined from the current logged-in user
    const response = await api.get('/payments/by-status/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching payments by status:', error);
    throw error;
  }
};

const getPaymentsByDate = async (params = {}) => {
  try {
    // The merchant is automatically determined from the current logged-in user
    const response = await api.get('/payments/by-date/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching payments by date:', error);
    throw error;
  }
};

const getPaymentsByMethod = async (params = {}) => {
  try {
    // The merchant is automatically determined from the current logged-in user
    const response = await api.get('/payments/by-method/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching payments by method:', error);
    throw error;
  }
};

// Public payment endpoints for customers
const initiatePayment = async (orderId, paymentMethodId, data = {}) => {
  try {
    const response = await api.post(`/payments/public/initiate/`, {
      order_id: orderId,
      payment_method_id: paymentMethodId,
      ...data
    });
    return response.data;
  } catch (error) {
    console.error(`Error initiating payment for order ${orderId}:`, error);
    throw error;
  }
};

const getPaymentStatus = async (paymentId) => {
  try {
    const response = await api.get(`/payments/public/${paymentId}/status/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching payment status for payment ${paymentId}:`, error);
    throw error;
  }
};

const getPaymentDetails = async (paymentId) => {
  try {
    const response = await api.get(`/payments/public/${paymentId}/details/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching payment details for payment ${paymentId}:`, error);
    throw error;
  }
};

const getAvailablePaymentMethods = async (merchantId) => {
  try {
    const response = await api.get('/payments/public/methods/', {
      params: { merchant: merchantId }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching available payment methods:', error);
    throw error;
  }
};

const markOrderAsPaid = async (orderId) => {
  try {
    const response = await api.post(`/orders/${orderId}/mark-paid/`);
    return response.data;
  } catch (error) {
    console.error(`Error marking order ${orderId} as paid:`, error);
    throw error;
  }
};

// GCash payment endpoints
const getGcashPaymentInfo = async (paymentId) => {
  try {
    const response = await api.get(`/payments/public/${paymentId}/gcash-info/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching GCash payment info for ${paymentId}:`, error);
    throw error;
  }
};

const uploadGcashReceipt = async (paymentId, imageFile) => {
  try {
    const formData = new FormData();
    formData.append('receipt_image', imageFile);
    const response = await api.post(
      `/payments/public/${paymentId}/upload-receipt/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    console.error(`Error uploading GCash receipt for ${paymentId}:`, error);
    throw error;
  }
};

const cancelGcashPayment = async (paymentId) => {
  try {
    const response = await api.post(`/payments/public/${paymentId}/gcash-cancel/`);
    return response.data;
  } catch (error) {
    console.error(`Error cancelling GCash payment ${paymentId}:`, error);
    throw error;
  }
};

const retryGcashPayment = async (orderId) => {
  try {
    const response = await api.post(`/payments/public/${orderId}/gcash-retry/`);
    return response.data;
  } catch (error) {
    console.error(`Error retrying GCash payment for order ${orderId}:`, error);
    throw error;
  }
};

const paymentService = {
  // Admin endpoints
  getPayments,
  getPayment,
  createPayment,
  updatePayment,
  processPayment,
  refundPayment,

  getPaymentMethods,
  getPaymentMethod,
  createPaymentMethod,
  updatePaymentMethod,
  deletePaymentMethod,

  getPaymentStatusHistory,

  // Statistics endpoints
  getPaymentStatistics,
  getPaymentsByStatus,
  getPaymentsByDate,
  getPaymentsByMethod,

  // Public endpoints
  initiatePayment,
  getPaymentStatus,
  getPaymentDetails,
  getAvailablePaymentMethods,

  // Order payment actions
  markOrderAsPaid,

  // GCash integration
  getGcashPaymentInfo,
  uploadGcashReceipt,
  retryGcashPayment,
  cancelGcashPayment,
};

export default paymentService;
